import { supabase } from '../lib/supabase';

export const journalEntriesService = {
  // Create new journal entry
  async createJournalEntry(entryData) {
    try {
      // Generate entry number
      const { data: numberData, error: numberError } = await supabase?.rpc('generate_journal_entry_number', { org_id: entryData?.organization_id });
      
      if (numberError) throw numberError;

      const { data, error } = await supabase?.from('journal_entries')?.insert([{
          ...entryData,
          entry_number: numberData
        }])?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Create journal entry error:', error);
      return { data: null, error: error?.message || 'Failed to create journal entry' };
    }
  },

  // Get journal entries for organization
  async getJournalEntries(organizationId, options = {}) {
    try {
      const { page = 1, limit = 20, status, dateFrom, dateTo, search } = options;
      const offset = (page - 1) * limit;

      let query = supabase?.from('journal_entries')?.select(`
          *,
          created_by:created_by(full_name, email),
          reviewed_by:reviewed_by(full_name, email),
          posted_by:posted_by(full_name, email),
          journal_entry_lines(
            id,
            account_id,
            description,
            debit_amount,
            credit_amount,
            line_order,
            chart_of_accounts:account_id(account_code, account_name)
          )
        `)?.eq('organization_id', organizationId)?.order('created_at', { ascending: false });

      if (status) query = query?.eq('status', status);
      if (dateFrom) query = query?.gte('entry_date', dateFrom);
      if (dateTo) query = query?.lte('entry_date', dateTo);
      if (search) {
        query = query?.or(`description.ilike.%${search}%,reference_number.ilike.%${search}%,entry_number.ilike.%${search}%`);
      }

      query = query?.range(offset, offset + limit - 1);

      const { data, error } = await query;
      if (error) throw error;

      return { data: data || [], error: null };
    } catch (error) {
      console.error('Get journal entries error:', error);
      return { data: [], error: error?.message || 'Failed to fetch journal entries' };
    }
  },

  // Update journal entry
  async updateJournalEntry(entryId, updates) {
    try {
      const { data, error } = await supabase?.from('journal_entries')?.update(updates)?.eq('id', entryId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Update journal entry error:', error);
      return { data: null, error: error?.message || 'Failed to update journal entry' };
    }
  },

  // Add line items to journal entry
  async addLineItems(journalEntryId, lineItems) {
    try {
      const itemsWithEntryId = lineItems?.map((item, index) => ({
        ...item,
        journal_entry_id: journalEntryId,
        line_order: item?.line_order || index + 1
      }));

      const { data, error } = await supabase?.from('journal_entry_lines')?.insert(itemsWithEntryId)?.select();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Add line items error:', error);
      return { data: null, error: error?.message || 'Failed to add line items' };
    }
  },

  // Delete line item
  async deleteLineItem(lineItemId) {
    try {
      const { error } = await supabase?.from('journal_entry_lines')?.delete()?.eq('id', lineItemId);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Delete line item error:', error);
      return { error: error?.message || 'Failed to delete line item' };
    }
  },

  // Approve journal entry
  async approveJournalEntry(entryId, userId) {
    try {
      const { data, error } = await supabase?.from('journal_entries')?.update({
          status: 'approved',
          reviewed_by: userId,
          reviewed_at: new Date()?.toISOString()
        })?.eq('id', entryId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Approve journal entry error:', error);
      return { data: null, error: error?.message || 'Failed to approve journal entry' };
    }
  },

  // Post journal entry
  async postJournalEntry(entryId, userId) {
    try {
      const { data, error } = await supabase?.from('journal_entries')?.update({
          status: 'posted',
          posted_by: userId,
          posted_at: new Date()?.toISOString()
        })?.eq('id', entryId)?.eq('status', 'approved')?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Post journal entry error:', error);
      return { data: null, error: error?.message || 'Failed to post journal entry' };
    }
  },

  // Reverse journal entry
  async reverseJournalEntry(entryId, userId, reason) {
    try {
      const { data, error } = await supabase?.from('journal_entries')?.update({
          status: 'reversed',
          reversed_by: userId,
          reversed_at: new Date()?.toISOString(),
          reversal_reason: reason
        })?.eq('id', entryId)?.eq('status', 'posted')?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Reverse journal entry error:', error);
      return { data: null, error: error?.message || 'Failed to reverse journal entry' };
    }
  },

  // Get journal entry by ID
  async getJournalEntryById(entryId) {
    try {
      const { data, error } = await supabase?.from('journal_entries')?.select(`
          *,
          created_by:created_by(full_name, email),
          reviewed_by:reviewed_by(full_name, email),
          posted_by:posted_by(full_name, email),
          reversed_by:reversed_by(full_name, email),
          journal_entry_lines(
            id,
            account_id,
            description,
            debit_amount,
            credit_amount,
            line_order,
            reference,
            chart_of_accounts:account_id(account_code, account_name, account_type, normal_balance)
          )
        `)?.eq('id', entryId)?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Get journal entry error:', error);
      return { data: null, error: error?.message || 'Failed to fetch journal entry' };
    }
  }
};